<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>Edit CPanel server</DialogTitle>
            </DialogHeader>
            <form @submit="onSubmit">
                <div class="space-y-3">
                    <FormField name="title" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Title (Required)</FormLabel>
                            <FormControl>
                                <Input
                                    v-bind="componentField"
                                    placeholder="Title"
                                    :loading="loading"
                                    focus
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="host" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Host (Required)</FormLabel>
                            <FormControl>
                                <Input
                                    v-bind="componentField"
                                    placeholder="Host"
                                    :loading="loading"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="username" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Username (Required)</FormLabel>
                            <FormControl>
                                <Input
                                    v-bind="componentField"
                                    placeholder="Username"
                                    :loading="loading"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="password" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Password (Required)</FormLabel>
                            <FormControl>
                                <Password
                                    v-bind="componentField"
                                    placeholder="Password"
                                    :can-generate="false"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="token" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Token (Optional)</FormLabel>
                            <FormControl>
                                <Password
                                    v-bind="componentField"
                                    placeholder="Token"
                                    :can-generate="false"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <DialogFooter>
                    <Button type="button" variant="secondary" :disabled="saving" @click="close"
                        >Cancel</Button
                    >
                    <Button
                        type="submit"
                        :loading="saving"
                        :disabled="meta.valid === false || meta.dirty === false"
                        >Save</Button
                    >
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input, Password } from "@/components/ui/input";
import { cpanelGraphQLClient } from "@/composables/default-urql-client";
import { toTypedSchema } from "@vee-validate/zod";
import gql from "graphql-tag";
import { useForm } from "vee-validate";
import { ref, watch } from "vue";
import { toast } from "vue-sonner";
import * as z from "zod";

const props = defineProps<{
    server: {
        id: string;
    };
}>();

const open = defineModel();

const emit = defineEmits(["success"]);
const loading = ref(false);
const saving = ref(false);

const schema = z.object({
    title: z.string().min(1, {
        message: "Title is required.",
    }),
    host: z.string().min(1, {
        message: "Host is required.",
    }),
    username: z.string().min(1, {
        message: "Username is required.",
    }),
    password: z.string().min(1, {
        message: "Password is required.",
    }),
    token: z.string().nullable(),
});

type Form = z.infer<typeof schema>;

const { handleSubmit, meta, resetForm } = useForm({
    validationSchema: toTypedSchema(schema),
});

watch(open, (isOpen) => {
    if (isOpen) {
        init();
    }
});

const init = async () => {
    loading.value = true;
    try {
        const { data, error } = await cpanelGraphQLClient.query(
            gql`
                query editCpanelServer($id: String!) {
                    server(id: $id) {
                        id
                        title
                        host
                        username
                        password
                        token
                    }
                }
            `,
            {
                id: props.server.id,
            }
        );

        if (data) {
            resetForm({
                values: {
                    title: data.server.title,
                    host: data.server.host,
                    username: data.server.username,
                    password: data.server.password,
                    token: data.server.token,
                },
            });
        }
    } finally {
        loading.value = false;
    }
};

const onSubmit = handleSubmit((values) => {
    save(values);
});

const close = () => {
    open.value = false;
};

const save = async (values: Form) => {
    saving.value = true;

    try {
        const { error } = await cpanelGraphQLClient.mutation(
            gql`
                mutation updateCpanelServer(
                    $id: String!
                    $title: String!
                    $host: String!
                    $username: String!
                    $password: String!
                    $token: String
                ) {
                    updateServer(
                        id: $id
                        title: $title
                        host: $host
                        username: $username
                        password: $password
                        token: $token
                    )
                }
            `,
            {
                id: props.server.id,
                title: values.title,
                host: values.host,
                username: values.username,
                password: values.password,
                token: values.token,
            }
        );

        if (!error) {
            toast.success("Success", {
                description: `Cpanel server was successfully updated.`,
            });

            emit("success");
            close();
        }
    } finally {
        saving.value = false;
    }
};
</script>
